import { useLogger } from '#layers/base/composables/useLogger'

interface ClinicChain {
  handle?: string
  colour: string
  name: string
  image?: string
  icon?: string
  description: Record<string, string>
  externalUrl?: string
  externalUrlTitle?: string
  contacts?: {
    name: string
    phone: string
    email: string
    image: string | null
    responsibleFor: {
      cities: string[]
      postalCodes: string[]
    }
  }[]
  supportedSuppliers?: string[]
  isNudgingEnabled: boolean
}

interface AffiliationResponse {
  gpoName: string
  clinicName: string
}

export const useClinicChainStore = defineStore('clinicChain', () => {
  const userStore = useUserStore()
  const { baseURL } = useRuntimeConfig().app
  const { $internalPublicApi } = useNuxtApp()
  const log = useLogger('useClinicChainStore')

  const error = computed(() => fetchError.value?.message || null)
  const supportedSuppliers = computed<string[]>(() => clinicChainData.value?.supportedSuppliers ?? [])

  const accessToken = computed(() => userStore.accessToken)
  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const gpoHandle = computed(() => userStore.gpoHandle)
  const abortIfNotAuthenticated = useAbortIfNotAuthenticated()

  const fetchGpoURL = computed(() => `/api/intern/gpos/${unref(gpoHandle)}`)

  const {
    data: clinicChainData,
    pending: isLoading,
    refresh: refreshClinicChain,
    error: fetchError,
  } = useFetch<ClinicChain>(
    fetchGpoURL,
    {
      headers: headerFields,
      baseURL,
      watch: [accessToken],
      ...abortIfNotAuthenticated,
      key: () => `clinic-chain-${unref(gpoHandle)}`,
      server: false,
      immediate: true,
      onResponseError({ response }) {
        log.warn('Failed to fetch clinic chain', { handle: unref(gpoHandle), responseStatus: response.status })
        // Fire-and-forget: refreshing the token flips `accessToken`, and the
        // `watch: [accessToken]` above performs the single recovery refetch.
        if (response.status === 401) void userStore.reloadAuth()
        if (response.status === 403) {
          setDefaultValues()
        }
      },
    },
  )

  const affiliationLoading = ref(false)
  const affiliationError = ref<string | null>(null)
  const affiliationResult = ref<AffiliationResponse | null>(null)

  async function updateAffiliation(gpoHandle: string, customerHandle: string, confirm: boolean) {
    affiliationLoading.value = true
    affiliationError.value = null
    affiliationResult.value = null

    try {
      affiliationResult.value = await $internalPublicApi<AffiliationResponse>(`/gpos/${gpoHandle}/affiliation`, {
        method: 'PATCH',
        body: {
          customerHandle,
          confirm,
        },
      })
    }
    catch (err: any) {
      log.warn('Failed to update affiliation status', { customerHandle: customerHandle, confirm: confirm, message: err.message })
      affiliationError.value = `Failed to update affiliation: ${err.message}`
      throw err
    }
    finally {
      affiliationLoading.value = false
    }
  }

  function setDefaultValues() {
    affiliationError.value = null
    affiliationResult.value = null
    clinicChainData.value = null
  }

  function $reset() {
    setDefaultValues()
    refreshClinicChain()
  }

  return {
    clinicChainData,
    supportedSuppliers,
    isLoading,
    updateAffiliation,
    refreshClinicChain,
    $reset,
    affiliationLoading,
    error,
    affiliationError,
    affiliationResult,
  }
})
